import { AlertTriangle, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store';
import { selectResult, selectBlockWarnings } from '@/store/selectors';
import { formatNumber, formatPercent } from '@/utils/format';
import { Button } from '@/components/ui/Button';
import { ScreenEmpty } from '@/components/ui/ScreenEmpty';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { CopyButton } from './CopyButton';
import { FormulaBreakdown } from './FormulaBreakdown';
import { InterpretationBand } from './InterpretationBand';
import { InlineSparkline } from './InlineSparkline';
import { Tooltip } from '@/components/ui/Tooltip';

/** Result + Formula + Interpretation card (always visible, right column, P1.6.1). */
export function ResultCard() {
  const result = useStore(selectResult);
  const blocks = useStore(useShallow(selectBlockWarnings));
  const sigFigs = useStore((s) => s.settings.significantFigures);
  const isComputing = useStore((s) => s.isComputing);
  const lastError = useStore((s) => s.lastError);
  const compute = useStore((s) => s.compute);
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const setSection = useStore((s) => s.setSection);
  const requestLoadDemo = useStore((s) => s.requestLoadDemo);
  const saveStudy = useStore((s) => s.saveStudy);
  const pushToast = useStore((s) => s.pushToast);

  // Supporting-material disclosure: expanded by default on desktop (≥768px),
  // collapsed on mobile so the answer (F value + actions + interpretation)
  // stays above the fold. Initial state follows the viewport at mount;
  // the user can toggle freely after that.
  const isDesktop = useIsDesktop();
  const [detailsOpen, setDetailsOpen] = useState(isDesktop);

  const sectionClass = `card result-card${isComputing ? ' computing' : ''}`.trim();

  return (
    <section className={sectionClass} aria-live="polite">
      <div className="result-mode-label">
        {result ? `${result.mode === 'absolute' ? 'Absolute F' : 'Relative F'} · ${result.aucBasis}` : 'Result'}
        {result ? (
          <Tooltip label="Bioavailability result">
            {result.mode === 'absolute'
              ? 'Absolute F is the fraction of the test dose reaching systemic circulation, relative to an IV reference (0–1, or 0–100%).'
              : 'Relative F is the dose-normalized AUC ratio of test vs reference formulation; may exceed 100%.'}
          </Tooltip>
        ) : null}
      </div>

      {isComputing ? (
        <div className="result-computing" role="status" aria-label="Computing result">
          <span className="btn-spinner" aria-hidden="true" />
          <span className="result-computing-text">Computing…</span>
        </div>
      ) : lastError && !result ? (
        <div className="result-blocked" role="alert">
          <AlertTriangle size={16} aria-hidden="true" /> {lastError}
          <div className="result-actions">
            <Button variant="secondary" size="sm" onClick={() => compute()}>
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSection('calculator.dataEntry', true);
                setTimeout(() => {
                  document
                    .getElementById('section-content-calculator\\.dataEntry')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
            >
              Go to data
            </Button>
          </div>
        </div>
      ) : blocks.length > 0 ? (
        <div className="result-blocked" role="alert">
          <AlertTriangle size={16} aria-hidden="true" /> Computation blocked: {blocks[0]!.message}
          <div className="result-actions">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setSection('calculator.dataEntry', true);
                setTimeout(() => {
                  document
                    .getElementById('section-content-calculator\\.dataEntry')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
            >
              Go to data
            </Button>
          </div>
        </div>
      ) : result ? (
        <>
          <div className="result-value">{formatNumber(result.fDoseCorrected, sigFigs)}</div>
          <div className="result-percent">{formatPercent(result.fPercent, sigFigs)}</div>
          <div className="result-actions">
            <CopyButton getText={() => `F = ${formatNumber(result.fDoseCorrected, sigFigs)} (${formatPercent(result.fPercent, sigFigs)})`} />
            <Button variant="primary" size="sm" onClick={() => setActiveScreen('analysis')}>
              Analyze
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setActiveScreen('charts')}>
              Open in Charts
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                saveStudy();
                pushToast('Study saved.', 'info');
              }}
            >
              Save
            </Button>
          </div>
          <InterpretationBand />

          <details
            className="result-details"
            open={detailsOpen}
            onToggle={(e) => setDetailsOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary className="result-details-summary">
              <ChevronRight
                size={12}
                aria-hidden="true"
                className={`result-details-chevron${detailsOpen ? ' open' : ''}`}
              />
              Show details
            </summary>
            <div className="result-details-body">
              {result.doseNormalized ? (
                <p className="field-hint">Dose-normalized (raw = {formatNumber(result.fRaw, sigFigs)})</p>
              ) : (
                <p className="field-hint">No dose correction applied (equal doses).</p>
              )}
              {result.cmaxDoseNormalizedRatio ? (
                <div className="stat-secondary">
                  <span className="stat-secondary-label">Cmax ratio (dose-normalized)</span>
                  <span className="stat-secondary-value">
                    {formatNumber(result.cmaxDoseNormalizedRatio.ratio, sigFigs)}{' '}
                    ({formatPercent(result.cmaxDoseNormalizedRatio.percent, sigFigs)})
                  </span>
                </div>
              ) : null}
              <InlineSparkline />
              <FormulaBreakdown />
            </div>
          </details>
        </>
      ) : (
        <ScreenEmpty
          title="No result yet"
          body="Enter concentration-time data for a test and reference formulation above, then press Ctrl+Enter to compute the bioavailability fraction (F)."
          primary={{ label: 'Open Calculator', onClick: () => setActiveScreen('calculator') }}
          secondary={{ label: 'Load Demo', onClick: requestLoadDemo }}
        />
      )}
    </section>
  );
}
